import { ColDef, ValueFormatterParams } from '@ag-grid-community/core';
import { inject, Injectable } from '@angular/core';
import { first } from 'lodash-es';
import { BehaviorSubject, combineLatest, EMPTY, Observable } from 'rxjs';
import { expand, reduce, tap } from 'rxjs/operators';

import { SitesService } from '@models/sites/sites.service';

import { AgCellWrapperComponent } from '@shared/ag-components/ag-cell-wrapper/ag-cell-wrapper.component';
import { TableConstants } from '@shared/constants/table.constants';
import {
  Currency,
  GqlService,
  InvestigatorCost,
  InvestigatorSummary,
  listPatientsQuery,
  listSitesQuery,
  PatientProtocolCategory,
} from '@shared/services/gql.service';
import { convertFilterToMap } from '@shared/utils';
import { Maybe, Utils } from '@shared/utils/utils';

export type InvestigatorSummaryRow = Record<string, Maybe<number | string>> & {
  amount_remaining: number;
  investigator: string;
  patient: Maybe<string>;
  site_id: string;
  site_name: string;
  site_no: string;
  total_cost_to_date: number;
};

@Injectable()
export class InvestigatorSummaryService {
  private gqlService = inject(GqlService);
  private sitesService = inject(SitesService);

  investigatorMap = new Map<string, string>();
  patientOptions$ = new BehaviorSubject<listPatientsQuery[]>([]);
  siteMap = new Map<string, listSitesQuery>();

  fetchAll() {
    const fetchParams = {
      page: {
        limit: 500,
        offset: 0,
      },
    };

    return this.gqlService
      .fetchInvestigatorSummaries$({
        ...fetchParams,
      })
      .pipe(
        expand(({ data, success }) => {
          if (success && data) {
            if (Number.isInteger(data.next_offset) && data.next_offset >= 0) {
              fetchParams.page.offset = data.next_offset;

              return this.gqlService.fetchInvestigatorSummaries$({
                ...fetchParams,
              });
            }
          }

          return EMPTY;
        }),
        reduce(
          (acc, curr) => {
            if (acc.data && curr.success && curr.data) {
              acc.data = acc.data.concat(curr.data.items);

              return acc;
            }

            return {
              data: null,
              errors: curr.errors,
              success: false,
            };
          },
          {
            data: [] as Array<InvestigatorSummary> | null,
            errors: [] as string[],
            success: true,
          }
        )
      );
  }

  getDefaultFilters(): Observable<
    [GraphqlResponse<Array<listSitesQuery>>, GraphqlResponse<Array<listPatientsQuery>>]
  > {
    return combineLatest([this.sitesService.get(), this.gqlService.listPatients$()]).pipe(
      tap(([siteList, patientList]) => {
        this.siteMap = convertFilterToMap<listSitesQuery>(siteList?.data || []);
        this.investigatorMap = new Map(
          (siteList?.data || []).map(({ id }) => [id, this.getPrincipalInvestigatorName(id)])
        );

        this.patientOptions$.next(
          (patientList.data || []).sort(
            ({ external_patient_id }, { external_patient_id: external_patient_id2 }) =>
              Utils.alphaNumSort(external_patient_id, external_patient_id2)
          )
        );
      })
    );
  }

  getGridData(response: GraphqlResponse<Array<InvestigatorSummary>>, dynamicColumns: ColDef[]) {
    const gridData: InvestigatorSummaryRow[] = [];

    if (response.success && response.data) {
      response.data.forEach((investigatorSummary) => {
        const row: InvestigatorSummaryRow = {
          amount_remaining: investigatorSummary.remaining_visit_costs || 0,
          forecast_cost_through_eot: 0,
          investigator: this.investigatorMap.get(investigatorSummary.site_id) || '',
          invoiceables: 0,
          patient: investigatorSummary.external_patient_id,
          patient_visit_total: 0,
          site_id: investigatorSummary.site_id,
          site_name: this.siteMap.get(investigatorSummary.site_id)?.name || '',
          site_no: this.siteMap.get(investigatorSummary.site_id)?.site_no || '',
          total_cost_to_date: 0,
        };
        let invoiceables = 0;
        let patient_visit_total = 0;

        dynamicColumns.forEach((column) => {
          row[`${column.field}`] = null;
        });

        investigatorSummary.investigator_costs.forEach((investigatorCost: InvestigatorCost) => {
          if (
            investigatorCost.cost_type ===
            PatientProtocolCategory.PATIENT_PROTOCOL_CATEGORY_INVOICEABLE
          ) {
            invoiceables += investigatorCost.cost;
          }

          if (
            investigatorCost.cost_type ===
            PatientProtocolCategory.PATIENT_PROTOCOL_CATEGORY_PATIENT_VISIT
          ) {
            if (investigatorCost.patient_group_id) {
              row[`patient_group::${investigatorCost.patient_group_id}`] = investigatorCost.cost;
            }

            patient_visit_total += investigatorCost.cost;
          }
        });

        row.invoiceables = invoiceables;
        row.patient_visit_total = patient_visit_total;
        row.total_cost_to_date = patient_visit_total + invoiceables;
        row.forecast_cost_through_eot = row.total_cost_to_date + row.amount_remaining;

        gridData.push(row);
      });
    }

    return gridData;
  }

  getGridDynamicColumns(response: GraphqlResponse<Array<InvestigatorSummary>>): ColDef[] {
    const columns_rank_order: { colDef: ColDef; rankOrder: number }[] = [];

    if (response.success && response.data) {
      response.data.forEach((investigatorSummary) => {
        investigatorSummary.investigator_costs.forEach((investigatorCost: InvestigatorCost) => {
          if (investigatorCost.patient_group_id) {
            const columnAlreadyExist = columns_rank_order.some((column) => {
              return column.colDef.field === `patient_group::${investigatorCost.patient_group_id}`;
            });

            if (!columnAlreadyExist) {
              const colDef = {
                ...TableConstants.dynamicColumnProps(investigatorCost.patient_group_name || ''),
                aggFunc: 'sum',
                cellClass: ['cell-right', 'cost', TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT],
                cellRenderer: AgCellWrapperComponent,
                field: `patient_group::${investigatorCost.patient_group_id}`,
                valueFormatter: (params: Pick<ValueFormatterParams, 'value'>) =>
                  params.value ? Utils.agCurrencyFormatter(params, Currency.USD) : Utils.zeroHyphen,
              };

              columns_rank_order.push({
                colDef: colDef,
                rankOrder: investigatorCost.patient_group_rank || 0,
              });
            }
          }
        });
      });
    }

    return columns_rank_order
      .sort((col1, col2) => col1.rankOrder - col2.rankOrder)
      .map((col) => {
        return col.colDef;
      });
  }

  private getPrincipalInvestigatorName(siteId: string): string {
    const principalInvestigator = first(this.siteMap.get(siteId)?.contacts);

    return principalInvestigator
      ? `${principalInvestigator.given_name} ${principalInvestigator.family_name}`.trim()
      : '';
  }
}
